import { useEffect, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ActivityDTO, BookingDTO } from "@shared/types";
import { addDays, diffDays, fmtDate, fmtDateTime, todayIn, fmtTime } from "@shared/dates";
import { can } from "@shared/domain";
import { fmtPhone, inr, plural } from "@shared/format";
import { fmtRate, listedAmount } from "@shared/tax";
import { api, errorMessage } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useChannelLook } from "@/lib/colors";
import { useOpenBooking } from "@/lib/nav";
import { useBooking, useBookingHistory, usePaymentMethods, useRooms, useUser } from "@/lib/queries";
import { confirm, toast } from "@/stores/feedback";
import { Sheet, SheetClose } from "@/components/overlays";
import {
  Button,
  ChannelChip,
  Checkbox,
  ColorDot,
  Countdown,
  DateStepper,
  EmptyState,
  ErrorState,
  Eyebrow,
  Facts,
  Field,
  Input,
  Notice,
  Select,
  Skeleton,
  StatusPill,
  Tabs,
  TextArea,
  RadioCards,
  StepperGroup,
} from "@/components/ui";
import { PhoneIcon } from "@/components/icons";
import { endpoints, type BookingAction } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";

export function BookingDetailHost() {
  const { booking } = useSearch({ strict: false });
  const open = useOpenBooking();
  return (
    <Sheet open={!!booking} onClose={() => open(null)} label="Booking">
      {booking && <BookingDetail code={booking} onClose={() => open(null)} />}
    </Sheet>
  );
}

function BookingDetail({ code, onClose }: { code: string; onClose: () => void }) {
  const q = useBooking(code);
  const [tab, setTab] = useState<"overview" | "history">("overview");
  const history = useBookingHistory(code, true);

  if (q.isPending)
    return (
      <div className="w-full">
        <Skeleton lines={6} className="p-6" />
      </div>
    );
  if (q.isError)
    return (
      <div className="p-6">
        <ErrorState title="Couldn't open that booking" body={errorMessage(q.error)} onRetry={() => void q.refetch()} />
        <Button className="mt-3" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  const b = q.data;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <DetailHeader b={b} onClose={onClose} />
      <div className="border-b border-line px-4 sm:px-6">
        <Tabs
          value={tab}
          onChange={setTab}
          options={[
            { value: "overview", label: "Overview" },
            { value: "history", label: "History" },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === "overview" ? <Overview b={b} history={history.data ?? []} /> : <History b={b} rows={history.data} loading={history.isPending} />}
      </div>
      <Actions b={b} />
    </div>
  );
}

function DetailHeader({ b, onClose }: { b: BookingDTO; onClose: () => void }) {
  const title = b.kind === "block" ? (b.blockReason ?? "Blocked") : (b.guest?.name ?? "Guest");
  const { lookup } = useChannelLook();
  return (
    <div className="border-b border-line-strong px-4 pb-3.5 pt-2 sm:px-6 min-[900px]:pt-5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[12px] tracking-[.1em] text-subtle">
            {b.code} · {b.propertyName}
          </div>
          <div className="font-display mt-0.5 break-words text-[26px] font-semibold leading-[1.15] min-[900px]:text-[30px]">{title}</div>
          {b.guest?.phone && (
            <>
              <div className="mt-1 hidden font-mono text-[14px] text-muted min-[900px]:block">{fmtPhone(b.guest.phone)}</div>
              {/* On the ground the phone is the most-used control on the screen. */}
              <a
                href={`tel:${b.guest.phone}`}
                className="mt-2.5 flex min-h-[48px] items-center gap-2.5 rounded-md border border-line-strong bg-sunk px-3 font-mono text-[16px] text-ink no-underline min-[900px]:hidden"
              >
                <PhoneIcon size={20} className="text-deodar" /> {fmtPhone(b.guest.phone)}
              </a>
            </>
          )}
        </div>
        <SheetClose onClose={onClose} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        <ChannelChip channel={b.channel} size="md" />
        {b.channel && <span className="text-[14px] text-muted">{lookup(b.channel).label}</span>}
        <StatusPill status={b.status} unacknowledged={!b.acknowledged} />
        {b.status === "hold" && b.holdExpiresAt && (
          <span className="font-mono text-[13px] text-brass">
            <Countdown until={b.holdExpiresAt} className="!text-brass" /> left
          </span>
        )}
      </div>
    </div>
  );
}

function EarlyFee({ b, editable }: { b: BookingDTO; editable: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(b.earlyCheckInFee ?? 0));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api(endpoints.bookings.detail(b.code), { method: "PATCH", body: { version: b.version, earlyCheckInFee: Number(value) || 0 } });
      await qc.invalidateQueries();
      toast.saved("Early check-in fee updated");
      setEditing(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        {b.earlyCheckInFee ? inr(b.earlyCheckInFee) : <span className="text-subtle">None</span>}
        {editable && (
          <button type="button" onClick={() => setEditing(true)} className="font-sans text-[12px] text-deodar underline">
            {b.earlyCheckInFee ? "Edit" : "Add"}
          </button>
        )}
      </span>
    );
  }
  return (
    <form
      className="inline-flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <span className="text-subtle">₹</span>
      <input
        autoFocus
        inputMode="numeric"
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/[^\d]/g, "").slice(0, 7))}
        aria-label="Early check-in fee"
        className="h-9 w-[90px] rounded-sm border border-line-strong bg-surface px-2 text-right font-mono text-[14px] focus:border-deodar focus:outline-none focus:ring-soft"
      />
      <Button type="submit" size="sm" variant="primary" loading={busy}>
        Save
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
        ✕
      </Button>
    </form>
  );
}

function stepActor(history: ActivityDTO[], match: RegExp): string | null {
  const e = [...history].reverse().find((h) => match.test(h.verb));
  return e ? e.actorName : null;
}

function Overview({ b, history }: { b: BookingDTO; history: ActivityDTO[] }) {
  const me = useUser();
  const money = can(me, "money.view");
  const tz = me.properties.find((p) => p.id === b.propertyId)?.timezone;
  const rows: [React.ReactNode, React.ReactNode, string?][] = [
    ["Room", `${b.roomName} · ${b.roomTypeName}${b.buildingName ? ` · ${b.buildingName}` : ""}`, "font-semibold"],
    ["Dates", `${fmtDate(b.checkIn)} → ${fmtDate(b.checkOut)}`, "font-mono"],
    ["Nights · party", `${plural(b.nights, "night")} · ${plural(b.guestsCount, "guest")}${b.extraBeds ? ` · ${plural(b.extraBeds, "extra bed")}` : ""}`, "font-mono"],
  ];
  // Meals and extras carry the colour picked for them on the Meals & extras page.
  if (b.mealPlan) {
    rows.push([
      "Meals",
      <span className="inline-flex items-center gap-2">
        <ColorDot color={b.mealPlan.color} />
        {b.mealPlan.name}
        {money && b.mealAmount ? <span className="font-mono text-muted">{inr(b.mealAmount)}</span> : null}
      </span>,
    ]);
  }
  for (const a of b.addons) {
    rows.push([
      "Extra",
      <span className="inline-flex items-center gap-2">
        <ColorDot color={a.color} />
        {a.label}
        {a.quantity > 1 ? ` × ${a.quantity}` : ""}
        {money && a.amount !== undefined ? <span className="font-mono text-muted">{inr(a.amount)}</span> : null}
      </span>,
    ]);
  }
  if (money && b.amount !== undefined) {
    if (b.extraBedsAmount) rows.push([`Extra beds · ${b.extraBeds} × ${b.nights}N`, inr(b.extraBedsAmount), "font-mono"]);
    // Set at check-in; a manager can correct it, or add one that was missed, afterwards.
    const feeEditable = can(me, "booking.editAmount") && !b.otaOwned && (b.status === "checked_in" || b.status === "checked_out");
    if (b.earlyCheckInFee || (feeEditable && b.status === "checked_in")) rows.push(["Early check-in", <EarlyFee b={b} editable={feeEditable} />, "font-mono"]);
    if (b.taxes?.length) {
      // Either way the stay earned amount − tax; inclusive only changes how the tax is read.
      rows.push(["Before tax", inr(b.amount - (b.taxAmount ?? 0)), "font-mono"]);
      for (const t of b.taxes) rows.push([`${b.taxInclusive ? "Incl. " : ""}${t.name} · ${fmtRate(t.rateBp)}`, inr(t.amount), "font-mono"]);
    }
    rows.push([b.taxes?.length ? "Total" : "Amount", inr(b.amount), "font-mono font-semibold"]);
    const received = (b.paid ?? 0) - (b.refunded ?? 0);
    if (b.paid) rows.push(["Received", inr(b.paid), "font-mono text-pine"]);
    if (b.refunded) rows.push(["Refunded", `− ${inr(b.refunded)}`, "font-mono text-brass"]);
    if (b.status === "cancelled" || b.status === "expired") {
      // Nothing is owed on a booking that no longer stands; what's left is what could go back.
      if (received > 0) rows.push(["Kept · refundable", inr(received), "font-mono font-semibold text-rhodo"]);
    } else {
      rows.push(["Balance due", inr(b.balance ?? 0), cn("font-mono font-semibold", (b.balance ?? 0) > 0 ? "text-rhodo" : "text-pine")]);
    }
  }
  if (b.externalRef) rows.push(["Channel ref", b.externalRef, "font-mono"]);

  const first = b.code.startsWith("H-") ? "Held" : b.channel === "mmt" ? "Imported" : b.kind === "block" ? "Blocked" : "Booked";
  const steps =
    b.kind === "block"
      ? [{ label: "Blocked", at: b.createdAt, who: b.createdByName, done: true }]
      : [
          { label: first, at: b.createdAt, who: b.createdByName ?? (b.channel === "mmt" ? "MakeMyTrip API" : null), done: true },
          { label: "Confirmed", at: b.confirmedAt, who: stepActor(history, /confirm|imported/), done: !!b.confirmedAt },
          { label: "Checked in", at: b.checkedInAt, who: stepActor(history, /checked the guest in/), done: !!b.checkedInAt },
          { label: "Checked out", at: b.checkedOutAt, who: stepActor(history, /checked the guest out/), done: !!b.checkedOutAt },
        ];
  if (b.status === "cancelled") steps.push({ label: "Cancelled", at: b.cancelledAt, who: stepActor(history, /cancel|released|removed/), done: true });
  if (b.status === "expired") steps.push({ label: "Hold lapsed", at: null, who: "System", done: true });

  return (
    <div>
      {b.kind === "stay" && <GuestNames b={b} />}
      {b.otaOwned && (
        <Notice tone="mmt" tag="MMT" className="border-x-0 border-t-0 px-4 sm:px-6">
          Dates, guest and amount are owned by MakeMyTrip and read-only here. Cancellation must be actioned on the MMT extranet, not in this system.
        </Notice>
      )}
      {b.inConflict && (
        <Notice tone="rhodo" tag="!" className="border-x-0 border-t-0 px-4 sm:px-6">
          Another claim sits on these nights.{" "}
          <Link to="/desk/conflicts" className="font-semibold text-rhodo underline">
            Resolve the conflict
          </Link>
        </Notice>
      )}
      <Facts rows={rows as never} />

      <div className="border-y border-line-strong px-4 py-5 sm:px-6">
        <Eyebrow className="mb-3">Status</Eyebrow>
        <ol className="flex flex-col gap-3">
          {steps.map((s) => (
            <li key={s.label} className="flex items-start gap-3">
              <span className={cn("mt-1 size-[13px] shrink-0 border-2", s.done ? (s.label === "Cancelled" || s.label === "Hold lapsed" ? "border-rhodo bg-rhodo" : "border-pine bg-pine") : "border-line-strong")} />
              <span className="flex-1">
                <span className={cn("block text-[15px] font-semibold", !s.done && "text-subtle")}>{s.label}</span>
                <span className="block font-mono text-[11.5px] text-subtle">
                  {s.who ?? "—"} · {s.at ? fmtDateTime(s.at, tz) : "—"}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </div>

      <NotesEditor b={b} />
    </div>
  );
}

function NotesEditor({ b }: { b: BookingDTO }) {
  const [value, setValue] = useState(b.notes);
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const qc = useQueryClient();
  // Notes the server sends back — this shift's save, or another host's — take
  // the box. Adjusted in render so the text never lags a frame behind.
  const [lastSaved, setLastSaved] = useState(b.notes);
  if (b.notes !== lastSaved) {
    setLastSaved(b.notes);
    setValue(b.notes);
  }
  const save = async () => {
    if (value === b.notes) return;
    setState("saving");
    try {
      await api(endpoints.bookings.detail(b.code), { method: "PATCH", body: { notes: value, version: b.version } });
      await qc.invalidateQueries({ queryKey: queryKeys.booking.detail(b.code) });
      setState("saved");
    } catch (e) {
      setState("idle");
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="px-4 py-5 sm:px-6">
      <Eyebrow className="mb-2.5 flex justify-between">
        <span>Notes</span>
        <span className="normal-case tracking-normal">{state === "saving" ? "Saving…" : state === "saved" ? "Saved" : "Saves when you leave the box"}</span>
      </Eyebrow>
      <TextArea value={value} onChange={(e) => setValue(e.target.value)} onBlur={save} placeholder="Arrival time, food, anything the next shift should know…" />
    </div>
  );
}

function History({ b, rows, loading }: { b: BookingDTO; rows?: ActivityDTO[]; loading: boolean }) {
  const me = useUser();
  const tz = me.properties.find((p) => p.id === b.propertyId)?.timezone;
  if (loading) return <Skeleton lines={5} className="p-6" />;
  return (
    <div>
      <div className="border-b border-line px-4 py-3 text-[13px] text-subtle sm:px-6">Everything that happened to {b.code}. Entries cannot be edited or removed.</div>
      {(rows ?? []).map((h, i) => (
        <div key={h.id} className={cn("border-b border-line px-4 py-3 sm:px-6", i % 2 ? "bg-sunk" : "bg-surface")}>
          <div className="font-mono text-[11.5px] text-subtle">{fmtDateTime(h.createdAt, tz)}</div>
          <div className="mt-0.5 text-[15px]">
            <strong className="font-semibold">{h.actorName}</strong> {h.verb}
          </div>
          {h.detail && <div className="mt-0.5 text-[13px] text-muted">{h.detail}</div>}
          {h.flag && <div className="mt-1.5 inline-block border border-rhodo px-1.5 py-0.5 text-[12.5px] text-rhodo">{h.flag}</div>}
          {h.diff && h.diff.length > 0 && (
            <div className="mt-2 overflow-hidden rounded-lg border border-line-strong">
              {h.diff.map((d) => (
                <div key={d.field} className="flex flex-wrap gap-2.5 border-b border-line bg-surface px-2.5 py-2 font-mono text-[12px] last:border-b-0">
                  <span className="flex-[1_1_90px] text-subtle">{d.field}</span>
                  <span className="flex-[1_1_90px] text-rhodo line-through">{d.from}</span>
                  <span className="flex-[1_1_90px] text-pine">{d.to}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {rows?.length === 0 && <EmptyState title="Nothing yet" body="History appears as soon as anyone acts on this booking." className="m-4" />}
    </div>
  );
}

/* ───────────────────────────── Actions ───────────────────────────── */

function Actions({ b }: { b: BookingDTO }) {
  const me = useUser();
  const qc = useQueryClient();
  const [panel, setPanel] = useState<null | "modify" | "pay" | "confirmPay" | "checkin" | "refund">(null);
  const [busy, setBusy] = useState<string | null>(null);
  const money = can(me, "money.view");
  const tz = me.properties.find((p) => p.id === b.propertyId)?.timezone;
  const today = todayIn(tz);
  const received = (b.paid ?? 0) - (b.refunded ?? 0);
  const canRefund = can(me, "booking.markPaid") && b.kind === "stay" && received > 0;

  const act = async (name: string, action: BookingAction, body: object = {}, done?: string) => {
    setBusy(name);
    try {
      await api(endpoints.bookings.action(b.code, action), { method: "POST", body: { version: b.version, ...body } });
      await qc.invalidateQueries();
      if (done) toast.saved(done);
      setPanel(null);
    } catch (e) {
      toast.error(errorMessage(e));
      void qc.invalidateQueries({ queryKey: queryKeys.booking.detail(b.code) });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Two writes in a row — names then check-in, confirm then payment — each
   * carrying the version the one before it returned.
   */
  const chain = async (name: string, steps: ((version: number) => Promise<BookingDTO>)[], done: string) => {
    setBusy(name);
    try {
      let version = b.version;
      for (const step of steps) version = (await step(version)).version;
      await qc.invalidateQueries();
      toast.saved(done);
      setPanel(null);
    } catch (e) {
      toast.error(errorMessage(e));
      void qc.invalidateQueries();
    } finally {
      setBusy(null);
    }
  };
  const post = (action: BookingAction, body: object) => (version: number) => api<BookingDTO>(endpoints.bookings.action(b.code, action), { method: "POST", body: { version, ...body } });

  const askCancel = () => {
    const isHold = b.status === "hold";
    const isBlock = b.kind === "block";
    confirm({
      title: isBlock ? "Remove this block?" : isHold ? "Release this hold?" : "Cancel a confirmed booking?",
      body: isBlock
        ? `${b.roomName} opens for ${fmtDate(b.checkIn)} → ${fmtDate(b.checkOut)} and the nights go back on sale.`
        : `This releases ${b.roomName} for ${fmtDate(b.checkIn)} → ${fmtDate(b.checkOut)} and puts the nights back on sale.${
            money && received > 0 ? ` ${inr(received)} has been received — record any refund on this booking afterwards.` : ""
          }`,
      verb: isBlock ? "Remove block" : isHold ? `Release ${b.code}` : `Yes, cancel ${b.code}`,
      run: async () => {
        await api(endpoints.bookings.action(b.code, "cancel"), { method: "POST", body: { version: b.version } });
        await qc.invalidateQueries();
        toast.cancelled(`${b.code} ${isHold ? "released" : "cancelled"} · nights reopened`);
      },
    });
  };

  const live = b.status === "hold" || b.status === "confirmed" || b.status === "checked_in";
  const refundPanel = (
    <RefundPanel
      propertyId={b.propertyId}
      received={received}
      busy={busy === "refund"}
      onRefund={(amount, method, reason) => act("refund", "refund", { amount, method, reason }, `Refund recorded · ${b.code}`)}
      onCancel={() => setPanel(null)}
    />
  );
  const canTakePayment = money && can(me, "booking.markPaid") && b.kind === "stay" && (b.balance ?? 0) > 0;
  /*
   * A finished stay still settles up: a balance left at checkout is taken
   * afterwards, and refunds outlive any stay. A cancelled one keeps refunds only.
   */
  if (!live) {
    const settle = b.status === "checked_out" && canTakePayment;
    if (!canRefund && !settle) return null;
    return (
      <div className="border-t border-line-strong bg-sunk pb-[env(safe-area-inset-bottom)]">
        {panel === "refund" && refundPanel}
        {panel === "pay" && <PayPanel b={b} busy={busy === "pay"} onPay={(amount, method) => act("pay", "mark-paid", { amount, method }, `Payment recorded · ${b.code}`)} onCancel={() => setPanel(null)} />}
        {!panel && (
          <div className="flex flex-wrap gap-2 px-4 py-3.5 sm:px-6">
            {settle && (
              <Button variant="primary" size="lg" className="flex-[1_1_160px]" onClick={() => setPanel("pay")}>
                Record payment · {inr(b.balance ?? 0)} due
              </Button>
            )}
            {canRefund && (
              <Button variant="outline" size="lg" className="flex-[1_1_160px]" onClick={() => setPanel("refund")}>
                Record a refund
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  const canCancelConfirmed = can(me, "booking.cancelConfirmed");
  const canCheckIn = (b.status === "confirmed" || b.status === "hold") && b.kind === "stay" && b.checkIn <= today && b.checkOut > today;

  return (
    <div className="border-t border-line-strong bg-sunk pb-[env(safe-area-inset-bottom)]">
      {panel === "modify" && <ModifyPanel b={b} onDone={() => setPanel(null)} />}
      {panel === "pay" && <PayPanel b={b} busy={busy === "pay"} onPay={(amount, method) => act("pay", "mark-paid", { amount, method }, `Payment recorded · ${b.code}`)} onCancel={() => setPanel(null)} />}
      {panel === "confirmPay" && (
        <PayPanel
          b={b}
          confirming
          busy={busy === "confirmPay"}
          onPay={(amount, method) => void chain("confirmPay", [post("confirm", {}), post("mark-paid", { amount, method })], `${b.code} confirmed · payment recorded`)}
          onCancel={() => setPanel(null)}
        />
      )}
      {panel === "refund" && refundPanel}
      {panel === "checkin" && (
        <CheckInPanel
          b={b}
          busy={busy === "checkin"}
          early={b.checkIn === today && !!b.checkInTime && fmtTime(new Date(), tz) < b.checkInTime}
          onSubmit={(idNote, earlyFee, occupants) =>
            void chain(
              "checkin",
              [
                ...(occupants?.length ? [(version: number) => api<BookingDTO>(endpoints.bookings.detail(b.code), { method: "PATCH", body: { version, occupants } })] : []),
                post("check-in", { idNote, ...(earlyFee !== undefined ? { earlyFee } : {}) }),
              ],
              `${b.guest?.name ?? b.code} checked in${earlyFee !== undefined ? " early" : ""}`,
            )
          }
          onCancel={() => setPanel(null)}
        />
      )}
      {!panel && (
        <div className="flex flex-wrap gap-2 px-4 py-3.5 sm:px-6">
          {!b.acknowledged && (
            <Button variant="outline" className="flex-[1_1_130px]" size="lg" loading={busy === "ack"} onClick={() => act("ack", "acknowledge", {}, `${b.code} acknowledged`)}>
              Acknowledge
            </Button>
          )}
          {b.status === "hold" && (
            <>
              {/* A hold usually turns firm when the money arrives: one step for both. */}
              {canTakePayment && (
                <Button variant="confirm" size="lg" className="kadil flex-[1_1_100%]" onClick={() => setPanel("confirmPay")}>
                  Confirm & record payment
                </Button>
              )}
              <Button
                variant={canTakePayment ? "secondary" : "confirm"}
                size="lg"
                className={cn("flex-[1_1_130px]", !canTakePayment && "kadil")}
                loading={busy === "confirm"}
                onClick={() => act("confirm", "confirm", {}, `${b.code} confirmed`)}
              >
                {canTakePayment ? "Confirm only" : "Confirm booking"}
              </Button>
              <Button size="lg" className="flex-[1_1_130px]" loading={busy === "extend"} onClick={() => act("extend", "extend-hold", {}, "Hold extended")}>
                Extend hold
              </Button>
            </>
          )}
          {canCheckIn && (
            <Button variant="primary" size="lg" className="flex-[1_1_130px]" onClick={() => setPanel("checkin")}>
              Check in
            </Button>
          )}
          {b.status === "checked_in" && (
            <Button
              variant="primary"
             
              size="lg"
              className="flex-[1_1_130px]"
              loading={busy === "checkout"}
              onClick={() =>
                confirm({
                  title: `Check ${b.guest?.name ?? "the guest"} out?`,
                  body:
                    b.checkOut > today
                      ? `They're leaving before ${fmtDate(b.checkOut)}. The remaining nights reopen for sale.`
                      : `${b.roomName} becomes free for tonight's arrivals.`,
                  verb: "Check out",
                  tone: "primary",
                  run: () => act("checkout", "check-out", {}, `${b.guest?.name ?? b.code} checked out`),
                })
              }
            >
              Check out
            </Button>
          )}
          {money && (b.balance ?? 0) > 0 && b.kind === "stay" && (
            <Button variant="outline" size="lg" className="flex-[1_1_130px]" onClick={() => setPanel("pay")}>
              Mark paid
            </Button>
          )}
          {canRefund && (
            <Button size="lg" className="flex-[1_1_130px]" onClick={() => setPanel("refund")}>
              Refund
            </Button>
          )}
          {b.kind === "stay" && (
            <Button size="lg" className="flex-[1_1_130px]" onClick={() => setPanel("modify")}>
              Modify
            </Button>
          )}
          {b.otaOwned ? (
            <Button disabled size="lg" variant="dashed" className="flex-[1_1_130px] !opacity-100 text-subtle" title="MakeMyTrip owns this cancellation">
              Cancel on MMT extranet
            </Button>
          ) : b.status === "hold" || b.kind === "block" || canCancelConfirmed ? (
            <Button variant="danger-outline" size="lg" className="flex-[1_1_130px]" onClick={askCancel}>
              {b.kind === "block" ? "Remove block" : b.status === "hold" ? "Release hold" : "Cancel booking"}
            </Button>
          ) : (
            <Button disabled size="lg" variant="dashed" className="flex-[1_1_130px] !opacity-100 text-subtle" title="Ask a manager to cancel confirmed bookings">
              Cancel — manager only
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Check in. Before the check-in time on the arrival day it offers an early
 * check-in: the fee is typed here (0 waives it) and goes on the bill. The
 * server lets it through only if the room was empty last night.
 */
function CheckInPanel({
  b,
  busy,
  early,
  onSubmit,
  onCancel,
}: {
  b: BookingDTO;
  busy: boolean;
  early: boolean;
  onSubmit: (idNote: string, earlyFee?: number, occupants?: { kind: "adult" | "child"; name: string }[]) => void;
  onCancel: () => void;
}) {
  const heads = headsOf(b);
  const [names, setNames] = useState<string[]>(() => namesOf(b));
  const naming = b.unnamedGuests > 0;
  const [idNote, setIdNote] = useState("");
  const [asEarly, setAsEarly] = useState(early);
  const [fee, setFee] = useState("");
  const n = Number(fee || 0);
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:px-6">
      <Eyebrow>Check in</Eyebrow>
      {naming && (
        <div className="flex flex-col gap-2">
          <div className="text-[13px] text-muted">Everyone staying — while they're at the desk. Any left blank can be added later.</div>
          {heads.map((h, i) => (
            <Input
              key={i}
              value={names[i]}
              onChange={(e) => setNames(names.map((n, j) => (j === i ? e.target.value : n)))}
              placeholder={h.label}
              aria-label={h.label}
              autoComplete="off"
              className="!min-h-[44px]"
            />
          ))}
        </div>
      )}
      <Input value={idNote} onChange={(e) => setIdNote(e.target.value)} placeholder="ID seen — e.g. Aadhaar last 4 · 7741 (optional)" />
      {early && (
        <div className="flex flex-col gap-2 rounded-md border border-line-strong bg-surface p-3">
          <Checkbox checked={asEarly} onChange={setAsEarly}>
            <span className="text-[14px] font-semibold">Early check-in</span>
            <span className="block text-[12.5px] text-subtle">Check-in is from {b.checkInTime}. Only possible if nobody had the room last night.</span>
          </Checkbox>
          {/* Whoever is at the desk takes the fee, so every role can type it. */}
          {asEarly && (
            <Field label="Early check-in fee (₹)" hint="0 to waive">
              {(id) => <Input id={id} mono inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value.replace(/[^\d]/g, ""))} placeholder="0" />}
            </Field>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          loading={busy}
          onClick={() =>
            onSubmit(
              idNote,
              early && asEarly ? n : undefined,
              naming ? heads.map((h, i) => ({ kind: h.kind, name: names[i].trim() })).filter((o) => o.name) : undefined,
            )
          }
        >
          {early && asEarly ? `Check in early${n ? ` · ${inr(n)}` : ""}` : "Check in now"}
        </Button>
        <Button size="lg" onClick={onCancel}>
          Back
        </Button>
      </div>
    </div>
  );
}

/**
 * The ways this booking's property takes money, from Settings › Payment
 * methods. A refund never goes back "to the platform": that's the platform's.
 */
function useMethods(propertyId: string, refund?: boolean) {
  const q = usePaymentMethods(propertyId);
  const options = (q.data?.methods ?? []).filter((m) => q.data!.accepted.includes(m.code) && !(refund && m.code === "ota")).map((m) => ({ value: m.code, label: m.label }));
  return { options, loading: q.isPending };
}

function MethodPicker({ name, propertyId, refund, value, onChange }: { name: string; propertyId: string; refund?: boolean; value: string; onChange: (v: string) => void }) {
  const { options, loading } = useMethods(propertyId, refund);
  // Default to the first method taken here once the list arrives.
  useEffect(() => {
    if (!value && options[0]) onChange(options[0].value);
  }, [value, options, onChange]);
  if (loading) return <Skeleton lines={1} />;
  if (options.length === 0) return <p className="text-[13.5px] text-rhodo">No payment methods are on for this property. Turn some on in Settings › Payment methods.</p>;
  return <RadioCards name={name} value={value} onChange={onChange} options={options} />;
}

function RefundPanel({
  propertyId,
  received,
  busy,
  onRefund,
  onCancel,
}: {
  propertyId: string;
  received: number;
  busy: boolean;
  onRefund: (amount: number, method: string, reason: string) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState(String(received));
  const [method, setMethod] = useState("");
  const [reason, setReason] = useState("");
  const n = Number(amount.replace(/[^\d]/g, ""));
  const tooMuch = n > received;
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:px-6">
      <Eyebrow>Record a refund · {inr(received)} received</Eyebrow>
      <Field label="Amount going back" error={tooMuch ? `Only ${inr(received)} has been received.` : null}>
        {(id) => <Input id={id} mono inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} />}
      </Field>
      <MethodPicker name="refund-method" propertyId={propertyId} refund value={method} onChange={setMethod} />
      <Field label="Reason" hint="kept in the booking's history">
        {(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Cancelled 10 days out — full refund per policy" maxLength={300} />}
      </Field>
      <div className="flex gap-2">
        <Button variant="danger-outline" size="lg" className="flex-1" loading={busy} disabled={!n || tooMuch || !reason.trim() || !method} onClick={() => onRefund(n, method, reason.trim())}>
          Refund {n && !tooMuch ? inr(n) : ""}
        </Button>
        <Button size="lg" onClick={onCancel}>
          Back
        </Button>
      </div>
    </div>
  );
}

function PayPanel({
  b,
  busy,
  onPay,
  onCancel,
  confirming,
}: {
  b: BookingDTO;
  busy: boolean;
  onPay: (amount: number, method: string) => void;
  onCancel: () => void;
  /** A hold being confirmed in the same breath as the money arrives. */
  confirming?: boolean;
}) {
  const [amount, setAmount] = useState(String(b.balance ?? 0));
  const [method, setMethod] = useState("");
  const n = Number(amount.replace(/[^\d]/g, ""));
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:px-6">
      <Eyebrow>
        {confirming ? "Confirm and record a payment" : "Record a payment"} · {inr(b.balance ?? 0)} due
      </Eyebrow>
      <Input mono inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount received" />
      <MethodPicker name="pay-method" propertyId={b.propertyId} value={method} onChange={setMethod} />
      <div className="flex gap-2">
        <Button variant="confirm" size="lg" className={cn("flex-1", confirming && "kadil")} loading={busy} disabled={!n || !method} onClick={() => onPay(n, method)}>
          {confirming ? `Confirm · ${n ? inr(n) : ""} received` : `Record ${n ? inr(n) : ""}`}
        </Button>
        <Button size="lg" onClick={onCancel}>
          Back
        </Button>
      </div>
    </div>
  );
}

/** One slot per head: the lead guest, the other adults, then the children. */
function headsOf(b: BookingDTO) {
  return [
    ...Array.from({ length: b.adults }, (_, i) => ({ kind: "adult" as const, label: i === 0 ? "Lead guest" : `Adult ${i + 1}` })),
    ...Array.from({ length: b.children }, (_, i) => ({ kind: "child" as const, label: `Child ${i + 1}` })),
  ];
}

/** The names already on the booking, slot by slot, the lead guest falling back to the guest record. */
function namesOf(b: BookingDTO): string[] {
  const known = (kind: "adult" | "child", i: number) => b.occupants.filter((o) => o.kind === kind)[i]?.name ?? "";
  return headsOf(b).map((h, i) => known(h.kind, h.kind === "adult" ? i : i - b.adults) || (i === 0 ? (b.guest?.name ?? "") : ""));
}

/**
 * Everyone staying, by name. A quick booking may hold only the lead guest's
 * name; the rest are named here whenever the desk has them — before arrival
 * or at the counter.
 */
function GuestNames({ b }: { b: BookingDTO }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const heads = headsOf(b);
  const [names, setNames] = useState<string[]>(() => namesOf(b));
  const [busy, setBusy] = useState(false);
  const live = b.status === "hold" || b.status === "confirmed" || b.status === "checked_in";

  const save = async () => {
    setBusy(true);
    try {
      await api(endpoints.bookings.detail(b.code), {
        method: "PATCH",
        // Only the names given: the rest can be added later.
        body: { version: b.version, occupants: heads.map((h, i) => ({ kind: h.kind, name: names[i].trim() })).filter((o) => o.name) },
      });
      await qc.invalidateQueries();
      toast.saved("Names saved");
      setEditing(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-line-strong px-4 py-3.5 sm:px-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <Eyebrow>Staying</Eyebrow>
        {live && !editing && (
          <button type="button" onClick={() => setEditing(true)} className="font-mono text-[11px] uppercase tracking-[.1em] text-deodar hover:underline">
            {b.unnamedGuests ? "Add names" : "Edit names"}
          </button>
        )}
      </div>
      {!editing ? (
        <div className="flex flex-wrap gap-1.5">
          {b.occupants.map((o) => (
            <span key={o.position} className="rounded-sm border border-line-strong bg-surface px-2 py-1 text-[13.5px]">
              {o.name}
              {o.kind === "child" && <span className="ml-1 font-mono text-[10px] text-subtle">CHILD</span>}
            </span>
          ))}
          {b.unnamedGuests > 0 && (
            <button type="button" onClick={() => live && setEditing(true)} className="border border-dashed border-brass px-2 py-1 text-[13px] text-brass">
              {plural(b.unnamedGuests, "guest")} not named yet
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {heads.map((h, i) => (
            <Input key={i} value={names[i]} onChange={(e) => setNames(names.map((n, j) => (j === i ? e.target.value : n)))} placeholder={h.label} aria-label={h.label} className="!min-h-[44px]" />
          ))}
          <div className="flex gap-2">
            <Button variant="primary" loading={busy} disabled={names.some((n) => !n.trim())} onClick={() => void save()}>
              Save names
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A −/+ count in the stepper style. */
function Counter({ label, value, min, max = 30, onChange }: { label: string; value: number; min: number; max?: number; onChange: (n: number) => void }) {
  const btn = "flex size-[34px] shrink-0 items-center justify-center rounded-full border border-line bg-sunk text-ink hover:bg-canvas disabled:opacity-35";
  return (
    <div className="min-w-[120px] flex-1 bg-surface px-2 py-1.5">
      <div className="font-mono text-[9px] uppercase tracking-[.12em] text-subtle">{label}</div>
      <div className="flex items-center justify-between gap-1">
        <button type="button" className={btn} disabled={value <= min} onClick={() => onChange(value - 1)} aria-label={`Fewer ${label.toLowerCase()}`}>
          −
        </button>
        <span className="font-mono text-[16px] font-semibold">{value}</span>
        <button type="button" className={btn} disabled={value >= max} onClick={() => onChange(value + 1)} aria-label={`More ${label.toLowerCase()}`}>
          +
        </button>
      </div>
    </div>
  );
}

function ModifyPanel({ b, onDone }: { b: BookingDTO; onDone: () => void }) {
  const me = useUser();
  const qc = useQueryClient();
  const money = can(me, "booking.editAmount");
  const [checkIn, setCheckIn] = useState(b.checkIn);
  const [checkOut, setCheckOut] = useState(b.checkOut);
  const [roomId, setRoomId] = useState(b.roomId);
  const [adults, setAdults] = useState(Math.max(1, b.adults));
  const [children, setChildren] = useState(b.children);
  const [extraBeds, setExtraBeds] = useState(b.extraBeds);
  // Edited as a listed price — before tax, or all-in if it was priced tax-inclusive; the booking's own taxes are worked back in on save.
  const preTax = b.amount !== undefined ? listedAmount({ amount: b.amount, taxAmount: b.taxAmount ?? 0, taxInclusive: !!b.taxInclusive }) : undefined;
  const [amount, setAmount] = useState(preTax !== undefined ? String(preTax) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rooms = useRooms(b.propertyId).data ?? [];

  const room = rooms.find((r) => r.id === roomId);
  const ota = b.otaOwned;
  const private_ = room?.kind !== "dorm";
  const bedsMax = private_ ? (room?.extraBedsMax ?? b.extraBeds) : 0;
  // Moving to a room that takes fewer extra beds drops the surplus.
  const bedsFor = Math.min(extraBeds, bedsMax);
  const sleeps = room ? (room.kind === "dorm" ? room.maxGuests * b.bedsCount : room.maxGuests + bedsFor) : undefined;
  const over = sleeps !== undefined && adults + children > sleeps;
  const lockedIn = b.status === "checked_in";

  const save = async () => {
    setBusy(true);
    setError(null);
    const body: Record<string, unknown> = { version: b.version };
    if (!ota && !lockedIn && checkIn !== b.checkIn) body.checkIn = checkIn;
    if (!ota && checkOut !== b.checkOut) body.checkOut = checkOut;
    if (roomId !== b.roomId) body.roomId = roomId;
    if (!ota && adults !== b.adults) body.adults = adults;
    if (!ota && children !== b.children) body.children = children;
    if (!ota && bedsFor !== b.extraBeds) body.extraBeds = bedsFor;
    if (money && !ota && amount !== "" && Number(amount) !== preTax) body.amount = Number(amount);
    try {
      await api(endpoints.bookings.detail(b.code), { method: "PATCH", body });
      await qc.invalidateQueries();
      toast.saved(`${b.code} updated`);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex max-h-[60dvh] flex-col gap-3 overflow-y-auto px-4 py-4 sm:px-6">
      <Eyebrow>Modify {b.code}</Eyebrow>
      {ota && <div className="text-[13px] text-muted">MakeMyTrip owns the dates, guest and amount. You can still move the guest to another room.</div>}
      {!ota && (
        <StepperGroup>
          {lockedIn ? (
            <div className="flex-1 bg-surface px-2 py-1.5">
              <div className="font-mono text-[9px] uppercase tracking-[.12em] text-subtle">In</div>
              <div className="font-mono text-[15px] font-semibold">{fmtDate(b.checkIn)}</div>
            </div>
          ) : (
            <DateStepper label="In" value={checkIn} onChange={(v) => { setCheckIn(v); if (checkOut <= v) setCheckOut(addDays(v, 1)); }} />
          )}
          <DateStepper label="Out" value={checkOut} min={addDays(checkIn, 1)} onChange={setCheckOut} />
        </StepperGroup>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="mb-1 text-[13px] text-muted">Room</div>
          <Select value={roomId} onChange={setRoomId} options={rooms.map((r) => ({ value: r.id, label: r.name, note: r.roomTypeName }))} />
        </div>
      </div>
      {!ota && b.kind === "stay" && (
        <div className="flex flex-col gap-2">
          <StepperGroup className="flex-wrap">
            <Counter label="Adults" value={adults} min={1} onChange={setAdults} />
            <Counter label="Children" value={children} min={0} onChange={setChildren} />
            {bedsMax > 0 && <Counter label={`Extra beds · max ${bedsMax}`} value={bedsFor} min={0} max={bedsMax} onChange={setExtraBeds} />}
          </StepperGroup>
          {sleeps !== undefined && (
            <div className={cn("text-[12.5px]", over ? "text-rhodo" : "text-subtle")}>
              {room?.name} sleeps {sleeps}
              {bedsFor ? ` with ${plural(bedsFor, "extra bed")}` : ""}
              {over ? ` — not ${adults + children}. ${bedsMax > bedsFor ? "Add an extra bed or pick" : "Pick"} a larger room.` : "."}
              {bedsMax === 0 && extraBeds > 0 && private_ ? ` The ${plural(extraBeds, "extra bed")} will be dropped.` : ""}
            </div>
          )}
        </div>
      )}
      {money && !ota && (
        <div>
          <div className="mb-1 text-[13px] text-muted">Amount {b.taxInclusive ? "incl. tax" : "before tax"} · leave as is to reprice from the rate calendar when dates change</div>
          <Input mono inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))} />
        </div>
      )}
      <div className="font-mono text-[12px] text-subtle">
        {fmtDate(checkIn)} → {fmtDate(checkOut)} · {plural(diffDays(checkIn, checkOut), "night")}
      </div>
      {error && (
        <Notice tone="rhodo" tag="TAKEN">
          {error}
        </Notice>
      )}
      <div className="flex gap-2">
        <Button variant="primary" size="lg" className="flex-1" loading={busy} disabled={over} onClick={save}>
          Save changes
        </Button>
        <Button size="lg" onClick={onDone}>
          Back
        </Button>
      </div>
    </div>
  );
}
